"use client";

import { c as _c } from "react/compiler-runtime";
import { DownOne, Sort, UpOne } from "@icon-park/svg";
import clsx from "clsx";
import { Children, FC, MouseEventHandler, PropsWithChildren, ReactNode, forwardRef, isValidElement } from "react";
import Icon from "../Icon";
import { ScrollArea, ScrollBar } from "../ScrollArea";
export type SortDirection = "unsorted" | "desc" | "asc" | false;
export interface TableProps {
  header?: ReactNode;
  totalRows?: number;
  border?: boolean;
  emptyMessage?: ReactNode;
  disableStateHandling?: boolean;
}
const tableCellPadding = "py-2xs px-2xs";
export type WithClassNameProps<T = any> = T & {
  className?: string;
};
export const TableHead = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function TableHead(t0, ref) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = clsx("z-10 sticky top-none bg-neutral-none after:absolute after:bottom-none after:left-none after:right-none after:h-[1px] after:bg-neutral-50 [&_tr:first-child]:border-t-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <thead ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
const TableHeadCell: FC<PropsWithChildren<WithClassNameProps<{
  colSpan?: number;
}>> & React.HTMLAttributes<HTMLTableCellElement>> = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let colSpan;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      colSpan,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = colSpan;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    colSpan = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = clsx("whitespace-nowrap text-neutral-675/60 font-normal text-left text-app-body-2 bg-neutral-0", "first:[&>div]:pl-s last:[&>div]:pr-s", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = clsx("h-full", tableCellPadding);
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <div className={t2}>{children}</div>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== colSpan || $[11] !== props || $[12] !== t1 || $[13] !== t3) {
    t4 = <th {...props} colSpan={colSpan} className={t1}>{t3}</th>;
    $[10] = colSpan;
    $[11] = props;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export interface SortableTableHeadCellProps {
  onSort: MouseEventHandler;
  isSorted: boolean | string;
  direction: SortDirection;
  id?: string;
}
const SortableTableHeadCell = t0 => {
  const $ = _c(12);
  const {
    id,
    children,
    onSort,
    direction,
    isSorted
  } = t0;
  let t1;
  if ($[0] !== isSorted) {
    t1 = clsx({
      "text-primary-800": isSorted
    });
    $[0] = isSorted;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !isSorted ? Sort : direction === "asc" ? UpOne : DownOne;
  let t3;
  if ($[2] !== t2) {
    t3 = <Icon icon={t2} className="ms-xs" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== onSort || $[6] !== t3) {
    t4 = <button className="flex items-center hover:text-primary-800 active:text-primary-850" onClick={onSort}>{children}{t3}</button>;
    $[4] = children;
    $[5] = onSort;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== id || $[9] !== t1 || $[10] !== t4) {
    t5 = <TableHeadCell id={id} className={t1}>{t4}</TableHeadCell>;
    $[8] = id;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const TableCell: FC<PropsWithChildren<WithClassNameProps<{
  colSpan?: number;
}>>> = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    colSpan
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = clsx(className, "text-app-body-2", tableCellPadding, "first:pl-s last:pr-s");
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== colSpan || $[4] !== t1) {
    t2 = <td colSpan={colSpan} className={t1}>{children}</td>;
    $[2] = children;
    $[3] = colSpan;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(function TableRow(t0, ref) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = clsx("group border-b border-neutral-50", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <tr ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
export const TableBody = forwardRef<HTMLTableSectionElement, PropsWithChildren<React.HTMLAttributes<HTMLTableSectionElement>>>(function TableBody(t0, ref) {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = clsx("", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <tbody ref={ref} {...props} className={t1}>{children}</tbody>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
const TableMobileView: FC<PropsWithChildren<{
  className?: string;
}>> = t0 => {
  const $ = _c(3);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className) {
    t1 = <div className={className}>{children}</div>;
    $[0] = children;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const Table = forwardRef<HTMLTableElement, PropsWithChildren<TableProps & React.HTMLAttributes<HTMLTableElement>>>(function Table(t0, ref) {
  const $ = _c(39);
  let T0;
  let emptyMessage;
  let hasContent;
  let hasMobileView;
  let header;
  let mobileView;
  let props;
  let t1;
  let tableChildren;
  if ($[0] !== t0) {
    const {
      children,
      totalRows,
      header: t2,
      border,
      emptyMessage: t3,
      disableStateHandling,
      ...t4
    } = t0;
    header = t2;
    emptyMessage = t3;
    props = t4;
    let t5;
    if ($[10] !== children) {
      const childArray = Children.toArray(children);
      mobileView = childArray.find(_temp);
      t5 = childArray.filter(_temp2);
      $[10] = children;
      $[11] = mobileView;
      $[12] = t5;
    } else {
      mobileView = $[11];
      t5 = $[12];
    }
    tableChildren = t5;
    hasMobileView = !!mobileView;
    hasContent = disableStateHandling || !!totalRows;
    T0 = ScrollArea;
    t1 = clsx("flex flex-col max-h-full border-neutral-50", {
      "border rounded-2xs": border,
      "m:border-t": hasMobileView,
      "border-t": !hasMobileView
    }, props.className);
    $[0] = t0;
    $[1] = T0;
    $[2] = emptyMessage;
    $[3] = hasContent;
    $[4] = hasMobileView;
    $[5] = header;
    $[6] = mobileView;
    $[7] = props;
    $[8] = t1;
    $[9] = tableChildren;
  } else {
    T0 = $[1];
    emptyMessage = $[2];
    hasContent = $[3];
    hasMobileView = $[4];
    header = $[5];
    mobileView = $[6];
    props = $[7];
    t1 = $[8];
    tableChildren = $[9];
  }
  let t2;
  if ($[13] !== hasMobileView || $[14] !== header) {
    t2 = header && <div className={clsx("px-s py-3xs", {
      "m:border-b m:border-neutral-50": hasMobileView,
      "border-b border-neutral-50": !hasMobileView
    })}>{header}</div>;
    $[13] = hasMobileView;
    $[14] = header;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== emptyMessage || $[17] !== hasContent || $[18] !== hasMobileView || $[19] !== mobileView) {
    t3 = hasMobileView && <div className="m:hidden">{hasContent ? mobileView : <div className="flex flex-col items-center gap-2xs mx-auto my-m text-center">{emptyMessage}</div>}</div>;
    $[16] = emptyMessage;
    $[17] = hasContent;
    $[18] = hasMobileView;
    $[19] = mobileView;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== hasContent || $[22] !== hasMobileView || $[23] !== props || $[24] !== ref || $[25] !== tableChildren) {
    t4 = hasContent && <table ref={ref} {...props} className={clsx("w-full", {
      "hidden m:table": hasMobileView
    })}>{tableChildren}</table>;
    $[21] = hasContent;
    $[22] = hasMobileView;
    $[23] = props;
    $[24] = ref;
    $[25] = tableChildren;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== emptyMessage || $[28] !== hasContent || $[29] !== hasMobileView) {
    t5 = !hasMobileView && !hasContent && <div className="flex flex-col items-center gap-2xs mx-auto my-m text-center">{emptyMessage}</div>;
    $[27] = emptyMessage;
    $[28] = hasContent;
    $[29] = hasMobileView;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ScrollBar orientation="horizontal" />;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== T0 || $[33] !== t1 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5) {
    t7 = <T0 className={t1}>{t2}{t3}{t4}{t5}{t6}</T0>;
    $[32] = T0;
    $[33] = t1;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
});
export default Object.assign(Table, {
  Cell: TableCell,
  Row: TableRow,
  HeadCell: TableHeadCell,
  SortableHeadCell: SortableTableHeadCell,
  Head: TableHead,
  Body: TableBody,
  MobileView: TableMobileView
});
function _temp(child) {
  return isValidElement(child) && child.type === TableMobileView;
}
function _temp2(child_0) {
  return isValidElement(child_0) && child_0.type !== TableMobileView;
}